import React, { useState, useEffect } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Avatar,
  Badge,
  Tooltip,
  Divider,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  useMediaQuery,
  useTheme,
  Chip
} from '@mui/material';
import {
  Settings as SettingsIcon,
  NotificationsNone as NotificationsIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  PointOfSale as PointOfSaleIcon,
  Balance as BalanceIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { getInvoiceMenuItems } from './DynamicInvoiceMenu';
import BranchSwitcher from './BranchSwitcher';
import axiosInstance from '../../utils/axiosConfig';

const AliphiaHeader: React.FC = () => {
  const { user, logout, activeBranch } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [hasDefaultPosPage, setHasDefaultPosPage] = useState<boolean>(false);
  const [defaultPosPageInfo, setDefaultPosPageInfo] = useState<{id: string, name: string} | null>(null);
  const [hasBalanceVoucherTemplate, setHasBalanceVoucherTemplate] = useState<boolean>(false);
  const [balanceVoucherTemplateInfo, setBalanceVoucherTemplateInfo] = useState<{id: string, name: string} | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [headerMessage, setHeaderMessage] = useState<string | null>(null);
  
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  // التحقق من وجود نقطة بيع افتراضية للفرع النشط
  useEffect(() => {
    const checkDefaultPosPage = async () => {
      if (activeBranch && activeBranch._id) {
        // الحصول على بيانات الفرع الكاملة من خلال API
        try {
          // استدعاء الواجهة البرمجية للحصول على تفاصيل الفرع النشط (مسار خاص لا يتطلب صلاحيات)
          const response = await fetch(`/api/branches/active-branch/${activeBranch._id}`);
          if (!response.ok) {
            throw new Error(`Error fetching branch data: ${response.status}`);
          }
          
          const branchData = await response.json();
          
          // التحقق من وجود قالب سند موازنة للفرع
          if (branchData && branchData.balanceVoucherTemplateId) {
            setHasBalanceVoucherTemplate(true);
            
            // جلب معلومات قالب سند الموازنة باستخدام السياق المناسب
            try {
              const { apiWithContext } = await import('../../services/api');
              const templateData = await apiWithContext.get(`/finance/specialized-vouchers/templates/${branchData.balanceVoucherTemplateId}`, 'specialized-voucher').then(res => res.data);
              setBalanceVoucherTemplateInfo({
                id: templateData._id || templateData.id,
                name: templateData.name || 'سند الموازنة'
              });
            } catch (templateError) {
              console.error('خطأ في الحصول على معلومات قالب سند الموازنة:', templateError);
              setBalanceVoucherTemplateInfo({
                id: branchData.balanceVoucherTemplateId,
                name: 'سند الموازنة'
              });
            }
          } else {
            setHasBalanceVoucherTemplate(false);
            setBalanceVoucherTemplateInfo(null);
          }
          
          if (branchData && branchData.defaultPosPage) {
            setHasDefaultPosPage(true);
            
            // محاولة الحصول على اسم صفحة الفاتورة من الخدمة
            try {
              const items = await getInvoiceMenuItems();
              if (Array.isArray(items)) {
                const posPage = items.find(item => item.id === branchData.defaultPosPage);
                
                if (posPage && posPage.id && posPage.text) {
                  setDefaultPosPageInfo({
                    id: posPage.id as string, // Ensure id is treated as string
                    name: posPage.text
                  });
                } else {
                  // صفحة لم يتم العثور عليها، لكن لدينا معرف - استخدم المعرف مباشرة
                  setDefaultPosPageInfo({
                    id: branchData.defaultPosPage,
                    name: 'نقطة البيع الافتراضية'
                  });
                }
              }
            } catch (menuError) {
              console.error('خطأ في الحصول على قائمة صفحات الفواتير:', menuError);
              // استخدم المعرف مباشرة في حالة وجود خطأ
              setDefaultPosPageInfo({
                id: branchData.defaultPosPage,
                name: 'نقطة البيع الافتراضية'
              });
            }
          } else {
            setHasDefaultPosPage(false);
            setDefaultPosPageInfo(null);
          }
        } catch (error) {
          console.error('خطأ في الحصول على معلومات نقطة البيع الافتراضية:', error);
          setHasDefaultPosPage(false);
          setDefaultPosPageInfo(null);
        }
      } else {
        setHasDefaultPosPage(false);
        setDefaultPosPageInfo(null);
      }
    };
    
    checkDefaultPosPage();
  }, [activeBranch]);

  // جلب رسالة القائمة العلوية من الإعدادات
  useEffect(() => {
    const fetchHeaderMessage = async () => {
      try {
        // استخدام المسار الجديد الذي لا يتطلب صلاحيات خاصة
        const response = await axiosInstance.get('/settings/general/header-message');
        if (response.data.success && response.data.data.headerMessage) {
          const { text, enabled, startDate, endDate } = response.data.data.headerMessage;
          
          // التحقق من تفعيل الرسالة
          if (enabled && text) {
            const now = new Date();
            
            // التحقق من التوقيت
            const isAfterStart = !startDate || new Date(startDate) <= now;
            const isBeforeEnd = !endDate || new Date(endDate) >= now;
            
            if (isAfterStart && isBeforeEnd) {
              setHeaderMessage(text);
            } else {
              setHeaderMessage(null);
            }
          } else {
            setHeaderMessage(null);
          }
        }
      } catch (error) {
        console.error('خطأ في جلب رسالة القائمة العلوية:', error);
        setHeaderMessage(null);
      }
    };

    fetchHeaderMessage();
    
    // تحديث الرسالة كل ساعة للتحقق من التوقيت
    const interval = setInterval(fetchHeaderMessage, 3600000);
    
    return () => clearInterval(interval);
  }, []);

  // الانتقال إلى صفحة إنشاء فاتورة جديدة مباشرة من نقطة البيع الافتراضية
  const goToDefaultPosPage = () => {
    if (hasDefaultPosPage && defaultPosPageInfo && defaultPosPageInfo.id) {
      // توجيه المستخدم مباشرة إلى صفحة إنشاء فاتورة جديدة بدلاً من الصفحة الرئيسية للفواتير
      navigate(`/invoices/${defaultPosPageInfo.id}/new`);
    }
  };

  // الانتقال إلى نافذة سند الموازنة المصغرة
  const openBalanceVoucher = () => {
    if (hasBalanceVoucherTemplate && balanceVoucherTemplateInfo && balanceVoucherTemplateInfo.id) {
      // فتح نافذة مصغرة لسند الموازنة
      navigate(`/finance/specialized-vouchers/compact/${balanceVoucherTemplateInfo.id}`);
    }
  };
  
  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: 'white',
        color: '#333',
        borderBottom: '1px solid',
        borderColor: 'divider',
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}
    >
      <Toolbar sx={{
        display: 'flex',
        justifyContent: 'space-between',
        height: { xs: 56, md: 64 },
        px: { xs: 1, sm: 1.5, md: 2 },
        minHeight: { xs: 56, md: 64 }
      }}>
        {/* Left side */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: 1, md: 'initial' } }}>
          {/* Branch Switcher */}
          <BranchSwitcher />
          
          {/* التاريخ - يخفى على الشاشات الصغيرة جداً */}
          {!isSmallMobile && (
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', mr: 2, ml: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                {new Date().toLocaleDateString('ar-SA', { weekday: 'long' })}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                {new Date().toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500, color: 'primary.main', fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                {new Date().toLocaleDateString('ar-SA-u-ca-islamic', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Center - Header Message - على الشاشات الكبيرة فقط */}
        {headerMessage && !isMobile && (
          <Box sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            px: 2,
            maxWidth: '60%'
          }}>
            <Chip
              icon={<CampaignIcon />}
              label={headerMessage}
              color="warning"
              sx={{
                maxWidth: '100%',
                height: 'auto',
                py: 1,
                px: 1.5,
                '& .MuiChip-label': {
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  lineHeight: 1.3
                },
                '& .MuiChip-icon': {
                  fontSize: '1.2rem'
                },
                boxShadow: '0 2px 8px rgba(237, 108, 2, 0.2)',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                  },
                  '50%': {
                    transform: 'scale(1.02)',
                  }
                }
              }}
            />
          </Box>
        )}
        
        {/* Right side */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Balance Voucher Button - على الجوال يظهر كأيقونة فقط */}
          {hasBalanceVoucherTemplate && balanceVoucherTemplateInfo && (
            isMobile ? (
              <Tooltip title={balanceVoucherTemplateInfo.name}>
                <IconButton
                  onClick={openBalanceVoucher}
                  sx={{
                    ml: 1,
                    color: 'white',
                    backgroundColor: '#9c27b0',
                    '&:hover': {
                      backgroundColor: '#7b1fa2',
                    },
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 }
                  }}
                >
                  <BalanceIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<BalanceIcon />}
                onClick={openBalanceVoucher}
                sx={{
                  ml: 2,
                  py: 0.8,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: '#9c27b0',
                  boxShadow: '0 4px 12px rgba(156, 39, 176, 0.2)',
                  '&:hover': {
                    backgroundColor: '#7b1fa2',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 15px rgba(156, 39, 176, 0.3)',
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {balanceVoucherTemplateInfo.name}
              </Button>
            )
          )}
          
          {/* Default POS Button - على الجوال يظهر كأيقونة فقط */}
          {hasDefaultPosPage && defaultPosPageInfo && (
            isMobile ? (
              <Tooltip title={defaultPosPageInfo.name}>
                <IconButton
                  onClick={goToDefaultPosPage}
                  sx={{
                    ml: 1,
                    color: 'white',
                    backgroundColor: '#30c3b0',
                    '&:hover': {
                      backgroundColor: '#25a899',
                    },
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 }
                  }}
                >
                  <PointOfSaleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PointOfSaleIcon />}
                onClick={goToDefaultPosPage}
                sx={{
                  ml: 2,
                  py: 0.8,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: '#30c3b0',
                  boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
                  '&:hover': {
                    backgroundColor: '#25a899',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 15px rgba(48, 195, 176, 0.3)',
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {defaultPosPageInfo.name}
              </Button>
            )
          )}
          
          {/* User Profile with Logout */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Avatar
              alt={user?.fullName || 'المستخدم'}
              sx={{
                width: { xs: 32, md: 34 },
                height: { xs: 32, md: 34 },
                ml: 1,
                boxShadow: '0 0 0 2px white, 0 0 0 4px #f0f0f0'
              }}
            />
            {/* معلومات المستخدم - تخفى على الشاشات الصغيرة جداً */}
            {!isSmallMobile && (
              <Box sx={{ ml: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                  {user?.fullName || 'المستخدم'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
                  {user?.isSystemAdmin ? 'مدير النظام' : user?.role === 'admin' ? 'مسؤول' : user?.role === 'manager' ? 'مدير' : 'موظف'}
                </Typography>
              </Box>
            )}
            <Tooltip title="تسجيل الخروج">
              <IconButton
                onClick={handleLogout}
                sx={{
                  ml: 1,
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: alpha('#d32f2f', 0.1)
                  }
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

        </Box>
      </Toolbar>
      
      {/* Header Message - على الجوال في صف منفصل */}
      {headerMessage && isMobile && (
        <Box sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1,
          px: 1,
          backgroundColor: 'rgba(255, 152, 0, 0.05)',
          borderTop: '1px solid rgba(255, 152, 0, 0.1)'
        }}>
          <Chip
            icon={<CampaignIcon />}
            label={headerMessage}
            color="warning"
            size="small"
            sx={{
              maxWidth: '95%',
              height: 'auto',
              py: 0.5,
              '& .MuiChip-label': {
                whiteSpace: 'normal',
                textAlign: 'center',
                fontSize: '0.75rem',
                fontWeight: 600,
                lineHeight: 1.3,
                padding: '4px 8px'
              },
              '& .MuiChip-icon': {
                fontSize: '1rem'
              },
              boxShadow: '0 2px 8px rgba(237, 108, 2, 0.2)'
            }}
          />
        </Box>
      )}
    </AppBar>
  );
};

export default AliphiaHeader;
